import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { HandHeart, HandCoins, Eye, Check, Loader2, Sparkles } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtRange } from "./TripSwitcher";

const OPTS = [
  { key: "prayer", label: "Pray", icon: HandHeart },
  { key: "financial", label: "Give", icon: HandCoins },
  { key: "follow", label: "Follow", icon: Eye },
];

// Shown once per journey after the missionary switches trips: partners are already on the new
// journey; their previous choices are pre-selected and one tap confirms (or adjusts) them.
export default function NewJourneyCard({ navy = "#0d2340", gold = "#E0A73A" }) {
  const { user, org, config, refreshMe, isLookingBack } = useAuth();
  const [sel, setSel] = useState(user?.partnership_ways || []);
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(false);

  const tid = config?.id;
  const switched = config?.made_current_at && user?.created_at && config.made_current_at > user.created_at;
  const acked = !!(user?.trip_acks && tid && user.trip_acks[tid]);
  if (!tid || !switched || acked || hidden || isLookingBack || user?.role !== "partner") return null;

  const first = (user?.name || "").split(" ")[0] || "friend";
  const changed = JSON.stringify([...sel].sort()) !== JSON.stringify([...(user?.partnership_ways || [])].sort());
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const confirm = async (ways) => {
    setSaving(true);
    try {
      await api.post("/me/trip-ack", { partnership_ways: ways });
      setHidden(true);
      toast.success(`So glad you're with us for ${config.trip_name}!`);
      await refreshMe();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="new-journey-card">
      <div className="rounded-3xl px-5 py-5 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg,#0d2340,#173a66)" }}>
        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5" style={{ color: gold }}><Sparkles className="h-3.5 w-3.5" /> A new journey begins</div>
        <h3 className="font-heading font-extrabold text-2xl mt-1.5 leading-tight">{config.trip_name}</h3>
        <p className="text-sm text-white/80 mt-2">{org?.org_name || "We"} {config.start_date ? `set out ${fmtRange(config.start_date, config.end_date)}` : "are setting out again"}. Thank you for walking the last journey with us.</p>
      </div>
      <div className="rounded-3xl px-5 py-5 bg-white border-2" style={{ borderColor: `${gold}66` }}>
        <h4 className="font-heading font-bold text-lg" style={{ color: navy }}>How will you partner this time, {first}?</h4>
        <p className="text-xs text-muted-foreground mt-1">We kept your choices from before — change anything, or just confirm.</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {OPTS.map((o) => {
            const on = sel.includes(o.key); const Icon = o.icon;
            return (
              <button key={o.key} type="button" data-testid={`nj-opt-${o.key}`} aria-pressed={on} onClick={() => toggle(o.key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${on ? "text-white" : "text-[#0d2340] bg-[#0d2340]/5"}`} style={on ? { background: gold, color: navy } : undefined}>
                <Icon className="h-4 w-4" /> {o.label} {on && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
        <button data-testid="nj-confirm" disabled={saving || sel.length === 0} onClick={() => confirm(sel)}
          className="mt-4 w-full rounded-full h-12 font-semibold text-sm flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-60" style={{ background: gold, color: navy }}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : changed ? "Update how I partner" : "Yes, count me in again"}
        </button>
        <button data-testid="nj-later" onClick={() => confirm(user?.partnership_ways || [])} className="w-full text-center text-xs text-muted-foreground mt-2 py-1">Keep it as it was</button>
      </div>
    </motion.div>
  );
}
